import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { copyToClipboard } from '../utils/clipboard';

/* ===========================================================================
 * MyCodes V2 — senior overhaul
 * - Header sticky padronizado (saldo + voltar + comprar)
 * - Filtros chips por rota + período
 * - Busca otimizada
 * - Cards de lote com preview e ações rápidas
 * - Modal de detalhes com cópia individual + CSV
 * - Empty / loading / error states refinados
 * - Mobile responsivo (3 breakpoints)
 * - Dark mode dedicado
 * ========================================================================= */

const ROUTES = {
  BR:  { label: 'BR',  title: 'Nacional',     color: '#22c55e', bg: 'rgba(34,197,94,0.13)',  flag: '🇧🇷' },
  CN:  { label: 'CN',  title: 'Importação',   color: '#ef4444', bg: 'rgba(239,68,68,0.13)',  flag: '🇨🇳' },
  INT: { label: 'INT', title: 'Internacional', color: '#3b82f6', bg: 'rgba(59,130,246,0.13)', flag: '🌐' },
};

const getRouteType = (codigo) => {
  if (!codigo) return 'BR';
  if (codigo.endsWith('INT')) return 'INT';
  if (codigo.endsWith('CN')) return 'CN';
  return 'BR';
};

const formatDate = (str) => {
  if (!str) return '—';
  const d = new Date(str.replace(' ', 'T'));
  if (isNaN(d)) return str;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (str) => {
  if (!str) return '—';
  const d = new Date(str.replace(' ', 'T'));
  if (isNaN(d)) return str;
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatRel = (str) => {
  if (!str) return '';
  const d = new Date(str.replace(' ', 'T'));
  if (isNaN(d)) return '';
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}m atrás`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h atrás`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d atrás`;
  const mo = Math.floor(days / 30);
  if (mo < 12) return `${mo}mês atrás`;
  return `${Math.floor(mo / 12)}a atrás`;
};

const MyCodes = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  /* ------- state ------- */
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [routeFilter, setRouteFilter] = useState('ALL'); // ALL | BR | CN | INT
  const [periodFilter, setPeriodFilter] = useState('all'); // all | 7d | 30d

  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [copiedBatch, setCopiedBatch] = useState(false);
  const [toast, setToast] = useState(null);

  const modalScrollRef = useRef(null);

  /* ------- fetch ------- */
  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const r = await api.get('/tracking/my-codes');
      setCodes(r.data || []);
    } catch (err) {
      console.error('Error fetching codes:', err);
      setError('Erro ao carregar códigos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCodes(); refreshUser(); }, [fetchCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onFocus = () => { fetchCodes(); refreshUser(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ------- toast ------- */
  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  }, []);

  /* ------- agrupamento ------- */
  const groupedBatches = useMemo(() => {
    const groups = codes.reduce((acc, code) => {
      const lote = code.lote_id || 'SEM_LOTE';
      if (!acc[lote]) acc[lote] = [];
      acc[lote].push(code);
      return acc;
    }, {});
    return Object.entries(groups)
      .map(([loteId, batchCodes]) => ({
        loteId,
        codes: batchCodes,
        firstCode: batchCodes[0],
        routeType: getRouteType(batchCodes[0]?.codigo),
      }))
      .sort((a, b) => new Date(b.firstCode?.created_at || 0) - new Date(a.firstCode?.created_at || 0));
  }, [codes]);

  /* ------- filtragem ------- */
  const filteredBatches = useMemo(() => {
    const q = search.toLowerCase().trim();
    const now = Date.now();
    const periodCutoff = periodFilter === '7d' ? now - 7 * 86400000 : periodFilter === '30d' ? now - 30 * 86400000 : 0;

    return groupedBatches
      .filter((b) => routeFilter === 'ALL' || b.routeType === routeFilter)
      .filter((b) => {
        if (!periodCutoff) return true;
        const t = new Date(b.firstCode?.created_at?.replace(' ', 'T') || 0).getTime();
        return t >= periodCutoff;
      })
      .map((batch) => ({
        ...batch,
        codes: q
          ? batch.codes.filter(
              (c) =>
                c.codigo.toLowerCase().includes(q) ||
                (c.destino && c.destino.toLowerCase().includes(q)) ||
                (c.origem && c.origem.toLowerCase().includes(q)) ||
                (batch.loteId && batch.loteId.toLowerCase().includes(q))
            )
          : batch.codes,
      }))
      .filter((b) => b.codes.length > 0);
  }, [groupedBatches, search, routeFilter, periodFilter]);

  /* ------- stats ------- */
  const stats = useMemo(() => {
    const total = codes.length;
    const last7 = codes.filter((c) => {
      const t = new Date(c.created_at?.replace(' ', 'T') || 0).getTime();
      return Date.now() - t <= 7 * 86400000;
    }).length;
    const byRoute = codes.reduce(
      (acc, c) => {
        const r = getRouteType(c.codigo);
        acc[r] = (acc[r] || 0) + 1;
        return acc;
      },
      { BR: 0, CN: 0, INT: 0 }
    );
    return { total, totalBatches: groupedBatches.length, last7, byRoute };
  }, [codes, groupedBatches]);

  /* ------- ações ------- */
  const copyCode = async (codigo, destino) => {
    const txt = destino ? `${codigo}: ${destino}` : codigo;
    const ok = await copyToClipboard(txt);
    if (ok) {
      setCopiedCode(codigo);
      showToast('success', `${codigo} copiado`);
      setTimeout(() => setCopiedCode(null), 2000);
    } else {
      showToast('error', 'Não foi possível copiar');
    }
  };

  const copyBatchAll = async (batch) => {
    const txt = batch.codes.map((c) => (c.destino ? `${c.codigo}: ${c.destino}` : c.codigo)).join('\n');
    const ok = await copyToClipboard(txt);
    if (ok) {
      setCopiedBatch(true);
      showToast('success', `${batch.codes.length} códigos copiados`);
      setTimeout(() => setCopiedBatch(false), 2000);
    } else {
      showToast('error', 'Não foi possível copiar');
    }
  };

  const downloadBatchCSV = (batch) => {
    const header = 'codigo,destino,origem,rota,criado_em\n';
    const rows = batch.codes
      .map((c) => `${c.codigo},"${c.destino || ''}","${c.origem || ''}",${getRouteType(c.codigo)},${c.data_criacao || c.created_at || ''}`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lote_${batch.loteId.replace(/[^a-z0-9-_]/gi, '_')}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('success', 'CSV baixado');
  };

  const openBatchModal = (batch) => {
    setSelectedBatch(batch);
    setShowBatchModal(true);
    setCopiedBatch(false);
  };

  const closeModal = useCallback(() => {
    setShowBatchModal(false);
    setTimeout(() => setSelectedBatch(null), 200);
  }, []);

  /* esc fecha modal */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && showBatchModal) closeModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showBatchModal, closeModal]);

  /* lock scroll body when modal opens */
  useEffect(() => {
    if (showBatchModal) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [showBatchModal]);

  const credits = user?.credits ?? 0;
  const hasFilters = search || routeFilter !== 'ALL' || periodFilter !== 'all';
  const clearFilters = () => {
    setSearch('');
    setRouteFilter('ALL');
    setPeriodFilter('all');
  };

  /* ===================================================================== */
  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{styles}</style>

      <div className="mc">
        {/* ========== HEADER ========== */}
        <header className="mc-header">
          <div className="mc-header-inner">
            <div className="mc-header-left">
              {/* Botão "Voltar" removido — navegação já fica na sidebar (ClientLayout) */}
              <div>
                <h1 className="mc-header-title">Meus Códigos</h1>
                <p className="mc-header-sub">{stats.total > 0 ? `${stats.total} código${stats.total !== 1 ? 's' : ''} gerados` : 'Histórico de rastreamentos'}</p>
              </div>
            </div>
            <div className="mc-header-right">
              <div className={`mc-balance-chip ${credits === 0 ? 'empty' : credits < 10 ? 'low' : ''}`}>
                <i className="fas fa-coins"></i>
                <div className="mc-balance-info">
                  <span className="mc-balance-label">Saldo</span>
                  <span className="mc-balance-val">{credits}</span>
                </div>
              </div>
              <button className="mc-cta-primary" onClick={() => navigate('/generate-code')}>
                <i className="fas fa-bolt"></i> <span>Gerar</span>
              </button>
            </div>
          </div>
        </header>

        {/* ========== TOAST ========== */}
        {toast && (
          <div className={`mc-toast mc-toast-${toast.type}`}>
            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : toast.type === 'error' ? 'fa-times-circle' : 'fa-exclamation-triangle'}`}></i>
            <span>{toast.msg}</span>
          </div>
        )}

        {/* ========== MAIN ========== */}
        <main className="mc-main">
          {loading ? (
            <div className="mc-state">
              <div className="mc-state-icon"><i className="fas fa-spinner fa-spin"></i></div>
              <h3>Carregando seus códigos...</h3>
              <p>Aguarde um instante</p>
            </div>
          ) : error ? (
            <div className="mc-state error">
              <div className="mc-state-icon error"><i className="fas fa-exclamation-triangle"></i></div>
              <h3>Erro ao carregar</h3>
              <p>{error}</p>
              <button className="mc-cta-primary" onClick={fetchCodes}>
                <i className="fas fa-redo"></i> Tentar novamente
              </button>
            </div>
          ) : codes.length === 0 ? (
            <div className="mc-state empty">
              <div className="mc-state-icon empty"><i className="fas fa-box-open"></i></div>
              <h3>Nenhum código ainda</h3>
              <p>Você ainda não gerou códigos de rastreamento.<br />Gere seu primeiro código agora.</p>
              <button className="mc-cta-primary big" onClick={() => navigate('/generate-code')}>
                <i className="fas fa-bolt"></i> Gerar primeiro código
              </button>
            </div>
          ) : (
            <>
              {/* ===== STATS ===== */}
              <div className="mc-stats">
                <div className="mc-stat">
                  <div className="mc-stat-icon orange"><i className="fas fa-qrcode"></i></div>
                  <div>
                    <div className="mc-stat-label">Total códigos</div>
                    <div className="mc-stat-val">{stats.total}</div>
                  </div>
                </div>
                <div className="mc-stat">
                  <div className="mc-stat-icon blue"><i className="fas fa-layer-group"></i></div>
                  <div>
                    <div className="mc-stat-label">Lotes</div>
                    <div className="mc-stat-val">{stats.totalBatches}</div>
                  </div>
                </div>
                <div className="mc-stat">
                  <div className="mc-stat-icon purple"><i className="fas fa-calendar-week"></i></div>
                  <div>
                    <div className="mc-stat-label">Últimos 7 dias</div>
                    <div className="mc-stat-val">{stats.last7}</div>
                  </div>
                </div>
                <div className="mc-stat">
                  <div className="mc-stat-icon green"><i className="fas fa-calendar-check"></i></div>
                  <div>
                    <div className="mc-stat-label">Último lote</div>
                    <div className="mc-stat-val small">{formatDate(groupedBatches[0]?.firstCode?.created_at)}</div>
                  </div>
                </div>
              </div>

              {/* ===== FILTROS ===== */}
              <div className="mc-filters">
                <div className="mc-search">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    placeholder="Buscar por código, destino, origem ou lote..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button className="mc-search-clear" onClick={() => setSearch('')} title="Limpar busca">
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>

                <div className="mc-chip-group">
                  <span className="mc-chip-label">Rota:</span>
                  <button
                    type="button"
                    className={`mc-chip ${routeFilter === 'ALL' ? 'active' : ''}`}
                    onClick={() => setRouteFilter('ALL')}
                  >
                    Todas <span className="mc-chip-count">{stats.total}</span>
                  </button>
                  {['BR', 'CN', 'INT'].map((r) => (
                    <button
                      type="button"
                      key={r}
                      className={`mc-chip route-${r} ${routeFilter === r ? 'active' : ''}`}
                      onClick={() => setRouteFilter(r)}
                      style={{ '--chip-color': ROUTES[r].color, '--chip-bg': ROUTES[r].bg }}
                    >
                      {ROUTES[r].flag} {r}
                      <span className="mc-chip-count">{stats.byRoute[r] || 0}</span>
                    </button>
                  ))}
                </div>

                <div className="mc-chip-group">
                  <span className="mc-chip-label">Período:</span>
                  {[
                    { k: 'all', l: 'Tudo' },
                    { k: '7d', l: '7 dias' },
                    { k: '30d', l: '30 dias' },
                  ].map((p) => (
                    <button
                      type="button"
                      key={p.k}
                      className={`mc-chip ${periodFilter === p.k ? 'active' : ''}`}
                      onClick={() => setPeriodFilter(p.k)}
                    >
                      {p.l}
                    </button>
                  ))}
                  {hasFilters && (
                    <button type="button" className="mc-chip-clear" onClick={clearFilters}>
                      <i className="fas fa-times"></i> Limpar
                    </button>
                  )}
                </div>
              </div>

              {/* ===== LISTA DE LOTES ===== */}
              {filteredBatches.length === 0 ? (
                <div className="mc-state empty">
                  <div className="mc-state-icon empty"><i className="fas fa-search"></i></div>
                  <h3>Nenhum resultado</h3>
                  <p>Tente ajustar os filtros ou a busca.</p>
                  <button className="mc-cta-secondary" onClick={clearFilters}>
                    <i className="fas fa-eraser"></i> Limpar filtros
                  </button>
                </div>
              ) : (
                <div className="mc-batches">
                  {filteredBatches.map((batch) => {
                    const rs = ROUTES[batch.routeType] || ROUTES.BR;
                    return (
                      <div key={batch.loteId} className="mc-batch">
                        <div className="mc-batch-head">
                          <div className="mc-batch-meta">
                            <div className="mc-batch-tag">
                              <span className="mc-route-pill" style={{ '--chip-color': rs.color, '--chip-bg': rs.bg }}>
                                {rs.flag} {rs.label}
                              </span>
                              <span className="mc-batch-count">
                                {batch.codes.length} código{batch.codes.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="mc-batch-id" title={batch.loteId}>{batch.loteId}</div>
                            <div className="mc-batch-date">
                              <i className="fas fa-calendar"></i>{' '}
                              {formatDateTime(batch.firstCode?.created_at)}{' '}
                              <span className="mc-batch-rel">· {formatRel(batch.firstCode?.created_at)}</span>
                            </div>
                          </div>
                          <div className="mc-batch-actions">
                            <button className="mc-icon-btn" onClick={() => copyBatchAll(batch)} title="Copiar todos">
                              <i className="fas fa-copy"></i>
                            </button>
                            <button className="mc-icon-btn" onClick={() => downloadBatchCSV(batch)} title="Baixar CSV">
                              <i className="fas fa-file-csv"></i>
                            </button>
                            <button className="mc-btn-primary" onClick={() => openBatchModal(batch)}>
                              <i className="fas fa-eye"></i> Detalhes
                            </button>
                          </div>
                        </div>

                        <div className="mc-batch-body">
                          {batch.codes.slice(0, 5).map((code) => (
                            <div key={code.id} className="mc-code-row">
                              <span className="mc-code-val">{code.codigo}</span>
                              <span className="mc-code-dest">
                                {code.destino ? (
                                  <><i className="fas fa-map-marker-alt"></i> {code.destino}</>
                                ) : (
                                  <span className="mc-muted">sem destino</span>
                                )}
                              </span>
                              <div className="mc-code-actions">
                                <button
                                  className="mc-icon-btn small"
                                  onClick={() => navigate(`/track?code=${code.codigo}`)}
                                  title="Rastrear"
                                >
                                  <i className="fas fa-map-marker-alt"></i>
                                </button>
                                <button
                                  className={`mc-icon-btn small ${copiedCode === code.codigo ? 'success' : ''}`}
                                  onClick={() => copyCode(code.codigo, code.destino)}
                                  title="Copiar"
                                >
                                  <i className={`fas ${copiedCode === code.codigo ? 'fa-check' : 'fa-copy'}`}></i>
                                </button>
                              </div>
                            </div>
                          ))}
                          {batch.codes.length > 5 && (
                            <button className="mc-more-btn" onClick={() => openBatchModal(batch)}>
                              <i className="fas fa-ellipsis-h"></i>
                              Ver +{batch.codes.length - 5} código{batch.codes.length - 5 !== 1 ? 's' : ''}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>

        {/* ========== MODAL DETALHES ========== */}
        {showBatchModal && selectedBatch && (
          <div className="mc-modal-overlay" onClick={closeModal}>
            <div className="mc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mc-modal-head">
                <div>
                  <h3 className="mc-modal-title">Detalhes do lote</h3>
                  <p className="mc-modal-sub">
                    {selectedBatch.codes.length} código{selectedBatch.codes.length !== 1 ? 's' : ''} ·{' '}
                    {ROUTES[selectedBatch.routeType]?.title || selectedBatch.routeType}
                  </p>
                </div>
                <button className="mc-modal-close" onClick={closeModal} title="Fechar (Esc)">
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="mc-modal-info">
                <div className="mc-modal-info-item">
                  <span>Lote</span>
                  <strong title={selectedBatch.loteId}>{selectedBatch.loteId}</strong>
                </div>
                <div className="mc-modal-info-item">
                  <span>Rota</span>
                  <strong>
                    {ROUTES[selectedBatch.routeType]?.flag} {selectedBatch.routeType}
                  </strong>
                </div>
                <div className="mc-modal-info-item">
                  <span>Origem</span>
                  <strong>{selectedBatch.firstCode?.origem || '—'}</strong>
                </div>
                <div className="mc-modal-info-item">
                  <span>Postado em</span>
                  <strong>{formatDate(selectedBatch.firstCode?.data_criacao)}</strong>
                </div>
              </div>

              <div className="mc-modal-body" ref={modalScrollRef}>
                {selectedBatch.codes.map((code, i) => (
                  <div key={code.id} className="mc-modal-row">
                    <span className="mc-modal-num">{i + 1}</span>
                    <span className="mc-modal-code">{code.codigo}</span>
                    <span className="mc-modal-dest">{code.destino || '—'}</span>
                    <div className="mc-modal-actions">
                      <button
                        className="mc-icon-btn small"
                        onClick={() => navigate(`/track?code=${code.codigo}`)}
                        title="Rastrear"
                      >
                        <i className="fas fa-map-marker-alt"></i>
                      </button>
                      <button
                        className={`mc-icon-btn small ${copiedCode === code.codigo ? 'success' : ''}`}
                        onClick={() => copyCode(code.codigo, code.destino)}
                        title="Copiar"
                      >
                        <i className={`fas ${copiedCode === code.codigo ? 'fa-check' : 'fa-copy'}`}></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mc-modal-footer">
                <button className="mc-btn-secondary" onClick={() => downloadBatchCSV(selectedBatch)}>
                  <i className="fas fa-file-csv"></i> Baixar CSV
                </button>
                <button className="mc-btn-primary" onClick={() => copyBatchAll(selectedBatch)}>
                  {copiedBatch ? <><i className="fas fa-check"></i> Copiados!</> : <><i className="fas fa-copy"></i> Copiar todos</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/* ===================================================================== */
const styles = `
  .mc *, .mc *::before, .mc *::after { margin: 0; padding: 0; box-sizing: border-box; }
  .mc {
    font-family: 'Lexend', sans-serif; color: #333;
    background: linear-gradient(180deg, #f5f5f7 0%, #ececef 100%);
    min-height: 100vh; -webkit-font-smoothing: antialiased;
  }

  /* ===== HEADER ===== */
  .mc-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    box-shadow: 0 4px 25px rgba(0,0,0,0.15); position: sticky; top: 0; z-index: 100;
  }
  .mc-header-inner {
    max-width: 1280px; margin: 0 auto; padding: 14px 24px;
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
  }
  .mc-header-left { display: flex; align-items: center; gap: 14px; }
  .mc-back-btn {
    width: 40px; height: 40px; border-radius: 10px;
    background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.10);
    color: #f90; cursor: pointer; transition: all .2s;
    display: flex; align-items: center; justify-content: center; font-size: 16px;
  }
  .mc-back-btn:hover { background: rgba(255,153,0,0.15); transform: translateX(-2px); }
  .mc-header-title { font-size: 19px; font-weight: 700; color: #fff; line-height: 1.1; }
  .mc-header-sub { font-size: 12px; color: rgba(255,255,255,0.55); margin-top: 2px; }
  .mc-header-right { display: flex; align-items: center; gap: 12px; }
  .mc-balance-chip {
    display: flex; align-items: center; gap: 12px;
    background: rgba(255,153,0,0.13); border: 1px solid rgba(255,153,0,0.30);
    padding: 8px 16px; border-radius: 12px; color: #fff; transition: all .3s;
  }
  .mc-balance-chip i { color: #f90; font-size: 18px; }
  .mc-balance-chip.low { background: rgba(245,158,11,0.18); border-color: rgba(245,158,11,0.50); }
  .mc-balance-chip.empty { background: rgba(229,62,62,0.18); border-color: rgba(229,62,62,0.50); }
  .mc-balance-info { display: flex; flex-direction: column; line-height: 1.1; }
  .mc-balance-label { font-size: 10px; color: rgba(255,255,255,0.6); text-transform: uppercase; letter-spacing: .5px; }
  .mc-balance-val { font-size: 17px; font-weight: 700; }
  .mc-cta-primary {
    background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border: none;
    padding: 9px 16px; border-radius: 10px; font-weight: 600; font-size: 13px;
    cursor: pointer; display: flex; align-items: center; gap: 6px;
    font-family: 'Lexend', sans-serif; transition: all .2s;
    box-shadow: 0 4px 14px rgba(255,153,0,0.25);
  }
  .mc-cta-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(255,153,0,0.40); }
  .mc-cta-primary.big {
    padding: 14px 28px; font-size: 15px; box-shadow: 0 8px 24px rgba(255,153,0,0.35);
  }

  /* ===== TOAST ===== */
  .mc-toast {
    position: fixed; top: 80px; left: 50%; transform: translateX(-50%);
    background: #1a1a2e; color: #fff; padding: 12px 22px; border-radius: 12px;
    display: flex; align-items: center; gap: 10px; z-index: 9999;
    box-shadow: 0 12px 40px rgba(0,0,0,0.25); font-weight: 500; font-size: 14px;
    animation: mcToastIn .25s ease;
  }
  .mc-toast i { font-size: 18px; }
  .mc-toast-success i { color: #22c55e; }
  .mc-toast-error i { color: #ef4444; }
  .mc-toast-warn i { color: #f59e0b; }
  @keyframes mcToastIn { from { opacity: 0; transform: translate(-50%, -10px); } to { opacity: 1; transform: translate(-50%, 0); } }

  /* ===== MAIN ===== */
  .mc-main { max-width: 1280px; margin: 0 auto; padding: 24px 24px 60px; }

  /* ===== STATS ===== */
  .mc-stats {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 22px;
  }
  .mc-stat {
    background: #fff; border-radius: 16px; padding: 16px 18px;
    display: flex; align-items: center; gap: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.04);
    transition: transform .2s, box-shadow .2s;
  }
  .mc-stat:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
  .mc-stat-icon {
    width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center; font-size: 18px;
  }
  .mc-stat-icon.orange { background: linear-gradient(135deg, #fff5e6, #ffedd5); color: #f90; }
  .mc-stat-icon.blue { background: rgba(59,130,246,0.12); color: #3b82f6; }
  .mc-stat-icon.purple { background: rgba(168,85,247,0.12); color: #a855f7; }
  .mc-stat-icon.green { background: rgba(34,197,94,0.12); color: #22c55e; }
  .mc-stat-label { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: .5px; font-weight: 600; }
  .mc-stat-val { font-size: 22px; font-weight: 800; color: #0f172a; line-height: 1.1; margin-top: 2px; }
  .mc-stat-val.small { font-size: 14px; font-weight: 700; }

  /* ===== FILTROS ===== */
  .mc-filters {
    background: #fff; border-radius: 16px; padding: 16px 18px; margin-bottom: 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.04);
    display: flex; flex-direction: column; gap: 12px;
  }
  .mc-search {
    display: flex; align-items: center; gap: 10px; background: #f8fafc;
    padding: 10px 14px; border-radius: 12px; border: 1.5px solid transparent; transition: all .2s;
  }
  .mc-search:focus-within { border-color: #f90; background: #fff; box-shadow: 0 0 0 3px rgba(255,153,0,0.12); }
  .mc-search i { color: #94a3b8; font-size: 14px; }
  .mc-search input {
    flex: 1; border: none; outline: none; background: transparent;
    font-family: 'Lexend', sans-serif; font-size: 14px; color: #222; min-width: 0;
  }
  .mc-search input::placeholder { color: #cbd5e1; }
  .mc-search-clear {
    width: 26px; height: 26px; border-radius: 8px; border: none;
    background: rgba(0,0,0,0.05); color: #64748b; cursor: pointer;
    display: flex; align-items: center; justify-content: center; font-size: 12px;
  }
  .mc-search-clear:hover { background: rgba(0,0,0,0.10); color: #222; }

  .mc-chip-group {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  }
  .mc-chip-label {
    font-size: 11px; font-weight: 700; color: #94a3b8;
    text-transform: uppercase; letter-spacing: .5px; margin-right: 4px;
  }
  .mc-chip {
    display: inline-flex; align-items: center; gap: 6px;
    background: #f8fafc; border: 1.5px solid #e8e8e8; border-radius: 10px;
    padding: 7px 13px; font-size: 13px; font-weight: 600; color: #555;
    cursor: pointer; font-family: 'Lexend', sans-serif; transition: all .2s;
  }
  .mc-chip:hover { border-color: #f90; color: #f90; }
  .mc-chip.active {
    background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border-color: transparent;
    box-shadow: 0 4px 14px rgba(255,153,0,0.30);
  }
  .mc-chip.active.route-BR { background: linear-gradient(135deg, #22c55e, #16a34a); box-shadow: 0 4px 14px rgba(34,197,94,0.30); }
  .mc-chip.active.route-CN { background: linear-gradient(135deg, #ef4444, #dc2626); box-shadow: 0 4px 14px rgba(239,68,68,0.30); }
  .mc-chip.active.route-INT { background: linear-gradient(135deg, #3b82f6, #2563eb); box-shadow: 0 4px 14px rgba(59,130,246,0.30); }
  .mc-chip-count {
    background: rgba(0,0,0,0.08); padding: 1px 8px; border-radius: 10px;
    font-size: 11px; font-weight: 700; min-width: 22px; text-align: center;
  }
  .mc-chip.active .mc-chip-count { background: rgba(255,255,255,0.25); color: #fff; }
  .mc-chip-clear {
    background: none; border: 1.5px dashed rgba(239,68,68,0.30); color: #ef4444;
    padding: 7px 13px; border-radius: 10px; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: 'Lexend', sans-serif;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .mc-chip-clear:hover { background: rgba(239,68,68,0.08); border-style: solid; }

  /* ===== STATES (loading / error / empty) ===== */
  .mc-state {
    background: #fff; border-radius: 20px; padding: 70px 30px; text-align: center;
    box-shadow: 0 4px 20px rgba(0,0,0,0.06); border: 1px solid rgba(0,0,0,0.04);
  }
  .mc-state-icon {
    width: 88px; height: 88px; border-radius: 24px; margin: 0 auto 22px;
    background: linear-gradient(135deg, #fff5e6, #ffedd5);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 10px 30px rgba(255,153,0,0.20);
  }
  .mc-state-icon i { font-size: 36px; color: #f90; }
  .mc-state-icon.error { background: linear-gradient(135deg, #fee, #fcc); box-shadow: 0 10px 30px rgba(239,68,68,0.20); }
  .mc-state-icon.error i { color: #ef4444; }
  .mc-state-icon.empty { background: linear-gradient(135deg, #f0f9ff, #e0f2fe); box-shadow: 0 10px 30px rgba(59,130,246,0.15); }
  .mc-state-icon.empty i { color: #3b82f6; }
  .mc-state h3 { font-size: 22px; color: #222; font-weight: 700; margin-bottom: 8px; }
  .mc-state p { font-size: 14px; color: #888; line-height: 1.6; margin-bottom: 24px; }
  .mc-cta-secondary {
    background: #fff; color: #f90; border: 2px solid #f90;
    padding: 12px 24px; border-radius: 12px; font-weight: 600; font-size: 14px;
    cursor: pointer; font-family: 'Lexend', sans-serif; transition: all .2s;
    display: inline-flex; align-items: center; gap: 8px;
  }
  .mc-cta-secondary:hover { background: #fff5e6; }

  /* ===== BATCHES ===== */
  .mc-batches { display: flex; flex-direction: column; gap: 16px; }
  .mc-batch {
    background: #fff; border-radius: 18px; overflow: hidden;
    box-shadow: 0 2px 14px rgba(0,0,0,0.05); border: 1px solid rgba(0,0,0,0.04);
    transition: box-shadow .25s, transform .15s;
  }
  .mc-batch:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.08); }
  .mc-batch-head {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    padding: 18px 22px; position: relative; overflow: hidden;
    display: flex; justify-content: space-between; align-items: center; gap: 14px;
  }
  .mc-batch-head::after {
    content: ''; position: absolute; top: -60%; right: -10%;
    width: 220px; height: 220px; border-radius: 50%;
    background: radial-gradient(circle, rgba(255,153,0,0.10), transparent 70%);
    pointer-events: none;
  }
  .mc-batch-meta { position: relative; z-index: 1; min-width: 0; flex: 1; }
  .mc-batch-tag {
    display: flex; align-items: center; gap: 10px; margin-bottom: 8px; flex-wrap: wrap;
  }
  .mc-route-pill {
    padding: 4px 10px; border-radius: 8px; font-size: 11px; font-weight: 700;
    letter-spacing: .5px; background: var(--chip-bg); color: var(--chip-color);
    border: 1px solid var(--chip-color);
  }
  .mc-batch-count {
    background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.85);
    padding: 4px 10px; border-radius: 8px; font-size: 11px; font-weight: 600;
  }
  .mc-batch-id {
    font-size: 14px; font-weight: 700; color: #fff;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    margin-bottom: 4px;
  }
  .mc-batch-date { font-size: 12px; color: rgba(255,255,255,0.55); }
  .mc-batch-date i { margin-right: 4px; font-size: 10px; }
  .mc-batch-rel { color: rgba(255,255,255,0.40); }
  .mc-batch-actions {
    display: flex; align-items: center; gap: 8px; position: relative; z-index: 1; flex-shrink: 0;
  }
  .mc-icon-btn {
    width: 38px; height: 38px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.85);
    cursor: pointer; transition: all .2s; font-family: 'Lexend', sans-serif;
    display: flex; align-items: center; justify-content: center; font-size: 14px;
  }
  .mc-icon-btn:hover { background: rgba(255,153,0,0.20); color: #fdba74; border-color: rgba(255,153,0,0.30); }
  .mc-icon-btn.small { width: 32px; height: 32px; font-size: 12px; border-color: #e2e8f0; background: #fff; color: #64748b; }
  .mc-icon-btn.small:hover { background: #fff5e6; color: #f90; border-color: #f90; }
  .mc-icon-btn.small.success { background: #22c55e !important; border-color: #22c55e !important; color: #fff !important; }
  .mc-btn-primary {
    background: linear-gradient(135deg, #f90, #ff6600); color: #fff; border: none;
    padding: 9px 16px; border-radius: 10px; font-weight: 600; font-size: 13px;
    cursor: pointer; display: flex; align-items: center; gap: 6px;
    font-family: 'Lexend', sans-serif; transition: all .2s;
    box-shadow: 0 4px 14px rgba(255,153,0,0.30);
  }
  .mc-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(255,153,0,0.40); }
  .mc-btn-secondary {
    background: #fff; color: #444; border: 1px solid #e0e0e0;
    padding: 9px 16px; border-radius: 10px; font-weight: 600; font-size: 13px;
    cursor: pointer; display: flex; align-items: center; gap: 6px;
    font-family: 'Lexend', sans-serif; transition: all .2s;
  }
  .mc-btn-secondary:hover { border-color: #f90; color: #f90; }

  .mc-batch-body { padding: 14px 20px; display: flex; flex-direction: column; gap: 8px; }
  .mc-code-row {
    display: grid; grid-template-columns: 200px 1fr auto; gap: 14px; align-items: center;
    padding: 10px 14px; background: #f8fafc; border-radius: 10px;
    border: 1px solid #f0f0f0; transition: all .2s;
  }
  .mc-code-row:hover { background: #fff8f0; border-color: rgba(255,153,0,0.20); }
  .mc-code-val {
    font-family: 'Courier New', monospace; font-weight: 700; color: #f90;
    font-size: 14px; letter-spacing: 0.3px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .mc-code-dest {
    font-size: 13px; color: #64748b;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .mc-code-dest i { color: #94a3b8; margin-right: 4px; font-size: 10px; }
  .mc-muted { color: #cbd5e1; font-style: italic; }
  .mc-code-actions { display: flex; gap: 6px; }

  .mc-more-btn {
    background: rgba(255,153,0,0.06); color: #f90; border: 1px dashed rgba(255,153,0,0.30);
    padding: 10px; border-radius: 10px; font-weight: 600; font-size: 12px;
    cursor: pointer; font-family: 'Lexend', sans-serif; transition: all .2s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .mc-more-btn:hover { background: rgba(255,153,0,0.13); border-style: solid; }

  /* ===== MODAL ===== */
  .mc-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.6); backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center; z-index: 2000;
    padding: 20px; animation: mcFadeIn .2s ease;
  }
  @keyframes mcFadeIn { from { opacity: 0; } to { opacity: 1; } }
  .mc-modal {
    background: #fff; border-radius: 20px; max-width: 720px; width: 100%;
    max-height: 90vh; display: flex; flex-direction: column;
    box-shadow: 0 30px 70px rgba(0,0,0,0.30);
    animation: mcSlideUp .25s ease; overflow: hidden;
  }
  @keyframes mcSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  .mc-modal-head {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 22px 26px 18px; border-bottom: 1px solid #f1f5f9;
  }
  .mc-modal-title { font-size: 20px; font-weight: 700; color: #222; }
  .mc-modal-sub { font-size: 13px; color: #888; margin-top: 4px; }
  .mc-modal-close {
    width: 36px; height: 36px; border-radius: 10px; background: #f1f5f9;
    border: none; cursor: pointer; color: #64748b; transition: all .2s;
    display: flex; align-items: center; justify-content: center; font-size: 14px;
  }
  .mc-modal-close:hover { background: #e2e8f0; color: #ef4444; }

  .mc-modal-info {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
    padding: 18px 26px; background: linear-gradient(135deg, #0f172a, #1e293b);
  }
  .mc-modal-info-item { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .mc-modal-info-item span { font-size: 10px; color: rgba(255,255,255,0.55); text-transform: uppercase; letter-spacing: .5px; font-weight: 600; }
  .mc-modal-info-item strong {
    font-size: 13px; color: #fff; font-weight: 600;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  .mc-modal-body {
    flex: 1; overflow-y: auto; padding: 12px 26px;
    display: flex; flex-direction: column; gap: 4px;
  }
  .mc-modal-row {
    display: grid; grid-template-columns: 28px 1fr 1fr auto; gap: 12px; align-items: center;
    padding: 12px 14px; border-bottom: 1px solid #f8fafc; border-radius: 8px;
    transition: background .15s;
  }
  .mc-modal-row:hover { background: #fafbfc; }
  .mc-modal-num { font-size: 11px; color: #94a3b8; font-weight: 600; text-align: center; }
  .mc-modal-code { font-family: 'Courier New', monospace; font-weight: 700; color: #f90; font-size: 14px; }
  .mc-modal-dest { font-size: 13px; color: #64748b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mc-modal-actions { display: flex; gap: 6px; }

  .mc-modal-footer {
    padding: 16px 26px; border-top: 1px solid #f1f5f9;
    display: flex; gap: 10px; justify-content: flex-end;
  }

  /* ============== RESPONSIVE ============== */
  /* Monitor estreito / vertical (~1080px): stats 4→2 e linha de código mais compacta */
  @media (max-width: 1280px) {
    .mc-stats { grid-template-columns: repeat(2, 1fr); }
    .mc-code-row { grid-template-columns: 180px 1fr auto; }
  }
  @media (max-width: 720px) {
    .mc-header-inner { padding: 12px 16px; flex-wrap: wrap; gap: 10px; }
    .mc-header-title { font-size: 17px; }
    .mc-header-sub { display: none; }
    .mc-header-right { width: 100%; justify-content: space-between; }
    .mc-balance-chip { padding: 6px 12px; gap: 8px; }
    .mc-balance-val { font-size: 15px; }
    .mc-main { padding: 18px 14px 50px; }
    .mc-stat-val { font-size: 18px; }
    .mc-filters { padding: 14px; }
    .mc-batch-head { flex-direction: column; align-items: flex-start; gap: 14px; padding: 16px; }
    .mc-batch-actions { width: 100%; justify-content: flex-end; }
    .mc-batch-body { padding: 12px 14px; }
    .mc-code-row {
      grid-template-columns: 1fr; gap: 6px; padding: 12px; align-items: flex-start;
    }
    .mc-code-val { font-size: 13px; white-space: normal; word-break: break-all; }
    .mc-code-dest { font-size: 12px; white-space: normal; }
    .mc-code-actions { width: 100%; justify-content: flex-end; margin-top: 4px; }
    .mc-modal-overlay { padding: 0; align-items: flex-end; }
    .mc-modal { max-width: 100%; max-height: 92vh; border-radius: 20px 20px 0 0; }
    .mc-modal-info { grid-template-columns: 1fr 1fr; }
    .mc-modal-row { grid-template-columns: 24px 1fr auto; gap: 10px; padding: 12px 8px; }
    .mc-modal-dest { display: none; }
    .mc-modal-footer { flex-direction: column-reverse; }
    .mc-modal-footer button { width: 100%; justify-content: center; }
  }
  @media (max-width: 480px) {
    .mc-stats { grid-template-columns: 1fr 1fr; }
    .mc-chip-label { display: none; }
    .mc-batch-id { font-size: 13px; }
    .mc-state { padding: 50px 22px; }
    .mc-state-icon { width: 72px; height: 72px; }
    .mc-state-icon i { font-size: 28px; }
    .mc-state h3 { font-size: 19px; }
  }
`;

export default MyCodes;
